import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { CalendarEvent } from "../hooks/useCalendar";

const CATEGORIES = [
  { value: "cultural", label: "Cultural" },
  { value: "local", label: "Local" },
  { value: "sector", label: "Sector" },
  { value: "seasonal", label: "Seasonal" },
  { value: "religious", label: "Religious" },
  { value: "commercial", label: "Commercial" },
  { value: "holiday", label: "Holiday" },
  { value: "awareness", label: "Awareness" },
  { value: "industry", label: "Industry" },
];

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

interface CalendarEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  onSave: (
    data: {
      event_name: string;
      event_month?: number | null;
      event_day?: number | null;
      end_month?: number | null;
      end_day?: number | null;
      movable?: boolean;
      date_year?: number | null;
      category: string;
      relevance: string;
      content_angle: string;
      planning_lead_days: number;
    }
  ) => Promise<void>;
}

const selectClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent";

function MonthDaySelects({
  month,
  day,
  onMonthChange,
  onDayChange,
  label,
}: {
  month: number | null;
  day: number | null;
  onMonthChange: (v: number | null) => void;
  onDayChange: (v: number | null) => void;
  label: string;
}) {
  const dayOptions: number[] = [];
  for (let d = 1; d <= 31; d++) {
    dayOptions.push(d);
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          {label} month
        </label>
        <select
          value={month ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            onMonthChange(val === "" ? null : parseInt(val, 10));
          }}
          className={selectClass}
        >
          <option value="">--</option>
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          {label} day
        </label>
        <select
          value={day ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            onDayChange(val === "" ? null : parseInt(val, 10));
          }}
          className={selectClass}
        >
          <option value="">--</option>
          {dayOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  isOpen,
  onClose,
  event,
  onSave,
}) => {
  const [eventName, setEventName] = useState("");
  const [eventMonth, setEventMonth] = useState<number | null>(null);
  const [eventDay, setEventDay] = useState<number | null>(null);
  const [endMonth, setEndMonth] = useState<number | null>(null);
  const [endDay, setEndDay] = useState<number | null>(null);
  const [movable, setMovable] = useState(false);
  const [dateYear, setDateYear] = useState<number | null>(null);
  const [category, setCategory] = useState("sector");
  const [relevance, setRelevance] = useState("");
  const [contentAngle, setContentAngle] = useState("");
  const [leadDays, setLeadDays] = useState(14);
  const [saving, setSaving] = useState(false);

  const isEditing = event !== null;

  useEffect(() => {
    if (isOpen && event) {
      setEventName(event.eventName);
      setEventMonth(event.eventMonth);
      setEventDay(event.eventDay);
      setEndMonth(event.endMonth);
      setEndDay(event.endDay);
      setMovable(event.movable);
      setDateYear(event.dateYear);
      setCategory(event.category);
      setRelevance(event.relevance || "");
      setContentAngle(event.contentAngle || "");
      setLeadDays(event.planningLeadDays);
    } else if (isOpen) {
      setEventName("");
      setEventMonth(null);
      setEventDay(null);
      setEndMonth(null);
      setEndDay(null);
      setMovable(false);
      setDateYear(null);
      setCategory("sector");
      setRelevance("");
      setContentAngle("");
      setLeadDays(14);
    }
  }, [isOpen, event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventName.trim()) return;

    setSaving(true);
    try {
      let resolvedYear = dateYear;
      if (movable && eventMonth !== null && eventDay !== null && resolvedYear === null) {
        const now = new Date();
        const thisYear = now.getFullYear();
        const target = new Date(thisYear, eventMonth - 1, eventDay);
        resolvedYear = target < now ? thisYear + 1 : thisYear;
      }

      await onSave({
        event_name: eventName.trim(),
        event_month: eventMonth,
        event_day: eventDay,
        end_month: endMonth,
        end_day: endDay,
        movable,
        date_year: movable ? resolvedYear : null,
        category,
        relevance: relevance.trim(),
        content_angle: contentAngle.trim(),
        planning_lead_days: leadDays,
      });
      onClose();
    } catch {
      // parent handles error
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Event" : "Add Event"}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="calendar-event-form"
            disabled={!eventName.trim()}
            isLoading={saving}
          >
            {isEditing ? "Save" : "Add Event"}
          </Button>
        </>
      }
    >
      <form id="calendar-event-form" onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Event name
          </label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g. International Coffee Day"
            required
            autoFocus
          />
        </div>

        <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-end">
          <MonthDaySelects
            month={eventMonth}
            day={eventDay}
            onMonthChange={setEventMonth}
            onDayChange={setEventDay}
            label="Start"
          />
          <MonthDaySelects
            month={endMonth}
            day={endDay}
            onMonthChange={setEndMonth}
            onDayChange={setEndDay}
            label="End"
          />
          <div>
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={movable}
                onChange={(e) => setMovable(e.target.checked)}
                className="rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs">Moves yearly</span>
            </label>
          </div>
        </div>
        <p className="text-xs text-gray-400 -mt-2">
          Leave empty if the date varies yearly. Check "Moves yearly" for holidays like Easter.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={selectClass}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content angle
          </label>
          <textarea
            value={contentAngle}
            onChange={(e) => setContentAngle(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
            placeholder="Suggested content idea for this event"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relevance
          </label>
          <textarea
            value={relevance}
            onChange={(e) => setRelevance(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
            placeholder="Why this matters for the business"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Planning lead days
          </label>
          <input
            type="number"
            value={leadDays}
            onChange={(e) => setLeadDays(parseInt(e.target.value, 10) || 14)}
            min={1}
            max={60}
            className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">
            7 for simple posts, 14-21 for campaigns
          </p>
        </div>
      </form>
    </Modal>
  );
};
